import * as React from 'react';
import { useInterval } from './useInterval';

interface IUseSlideshow {
  items: Array<any>;
  autoplay: boolean;
  duration: number;
  banners: Array<any>;
  lazyLoad: number | boolean;
  changed?: (slide: any) => void;
}

interface ISlide {
  ref: React.Ref<any>;
  id: string;
  text?: string;
  attr?: {
    alt?: string;
    'data-lazy-slide-url'?: string;
    onClick?: () => void;
  };
}

export const useSlideshow = (args: IUseSlideshow) => {
  const isInit = React.useRef<boolean | null>(null);
  const [autoplay, setAutoplay] = React.useState(false);
  const [current, setActiveSlide] = React.useState(0);
  const [slides, setSlides] = React.useState<Array<ISlide>>([]);
  const [elements, setElements] = React.useState<Array<any>>([]);

  React.useEffect(() => {
    if (autoplay !== args.autoplay) {
      setAutoplay(args.autoplay);
    }
  }, [args.autoplay]);

  React.useEffect(() => {
    if (args.items && isInit) {
      init();
    }
  }, [isInit, args.items]);

  React.useEffect(() => {
    if (!isInit.current && elements.length) {
      navigation();
    }
  }, [isInit.current, elements, current]);

  const getNextIndex = () => {
    return current === slides.length - 1 ? 0 : current + 1;
  };

  const getPrevIndex = () => {
    return current === 0 ? slides.length - 1 : current - 1;
  };

  // Autoplay

  useInterval({
    cb: () => {
      const nextIndex = getNextIndex();
      setActiveSlide(nextIndex);
    },
    delay: autoplay ? args.duration : null,
  });

  const handleCreateRef = (e: HTMLElement) => {
    setElements((prev) => [...prev, e]);
  };

  const init = () => {
    if (args.items) {
      const arr = args.items.map((item, index) => {
        return {
          ...item,
          id: `slide_${index}`,
          ref: handleCreateRef,
          attr: {
            alt: item.text ? item.text : 'ingen beskrivelse',
            'data-lazy-slide-url': item.url,
          },
        };
      });

      setSlides(arr);
      isInit.current = false;
    }
  };

  // Set src and active class when navigating

  const lazySetImageSrc = ({ img }: { img: any }) => {
    if (img) {
      const url = img.dataset['lazySlideUrl'];
      if (typeof url === 'string') {
        img.setAttribute('src', url);
      }
    }
  };

  const navigation = () => {
    const items = [...elements];
    items.map((item, index) => {
      const imgTag = item.querySelectorAll('img')[0];
      if (index < current + 2) {
        lazySetImageSrc({ img: imgTag });
      }
      if (index === current) {
        const imgTag = item.querySelectorAll('img')[0];
        lazySetImageSrc({ img: imgTag });
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });
  };

  const pause = () => {
    setAutoplay(false);
    setActiveSlide(0);
    if (typeof args.changed === 'function') {
      args.changed(0);
    }
  };

  const play = () => {
    setAutoplay(true);
  };

  const togglePlay = () => {
    setAutoplay((prev) => !prev);
  };

  const handlePrev = () => {
    const prevSlide = getPrevIndex();
    setActiveSlide(prevSlide);
    setAutoplay(false);
    if (typeof args.changed === 'function') {
      args.changed(prevSlide);
    }
  };

  const handleNext = () => {
    const nextSlide = getNextIndex();
    setActiveSlide(nextSlide);
    setAutoplay(false);
    if (typeof args.changed === 'function') {
      args.changed(nextSlide);
    }
  };

  const select = (index: number) => {
    setActiveSlide(index);
    setAutoplay(false);
    if (typeof args.changed === 'function') {
      args.changed(index);
    }
  };

  return {
    select,
    slides,
    current,
    handleNext,
    handlePrev,
    isPlaying: autoplay,
    play,
    pause,
    togglePlay,
  };
};
